import { OPTIMIZE_ANALYSES } from './optimizeCampaignRun';
import { buildOptimizerDigest, extractSuggestions } from '../components/CampaignLaunchWorkflow/agent/optimizerDigest';
import { appliedSuggestionIndexes } from './suggestionApplication';

export const ANALYSIS_LABELS = {
  diagnostic: 'Diagnosis', pacing: 'Budget pacing', performance: 'Performance',
};

const metadata = (message) => {
  const value = message.source_file_metadata || message.file_metadata;
  if (typeof value !== 'string') return value || {};
  try { return JSON.parse(value); } catch (_) { return {}; }
};

/** Only reuse an explicitly grouped run; never combine unrelated historical reports. */
export function latestFullAnalysis(messages) {
  const grouped = messages.filter((m) => (m.message_type === 'optimize-campaign'
    || m.action === 'optimize-campaign') && metadata(m).analysisBatchId)
    .sort((a, b) => new Date(b.message_created_at || b.associated_at)
      - new Date(a.message_created_at || a.associated_at));
  if (!grouped.length) return null;
  const batchId = metadata(grouped[0]).analysisBatchId;
  const members = grouped.filter((m) => metadata(m).analysisBatchId === batchId);
  return {
    batchId,
    runs: OPTIMIZE_ANALYSES.flatMap(({ kind }) => {
      const m = members.find((item) => metadata(item).analysisKind === kind);
      return m ? [{ kind, messageId: m.message_id, status: m.status,
        result: m.json_response, createdAt: m.message_created_at || m.associated_at }] : [];
    }),
  };
}

/** Keep source indexes separate from the combined, positional agent indexes. */
export function combineAnalysisRuns(runs, batchId) {
  const actionable = [];
  const summaries = [];
  const insights = [];
  const keyMetrics = {};
  let platformCampaignId = null;
  for (const run of runs) {
    const digest = buildOptimizerDigest(run.result);
    platformCampaignId = platformCampaignId || digest.platformCampaignId;
    const label = ANALYSIS_LABELS[run.kind] || run.kind;
    summaries.push(`${label}:\n${digest.summary || 'See the complete report in analyses.'}`);
    insights.push(...digest.insights.map((i) => ({ ...i, insight: `${label}: ${i.insight}` })));
    keyMetrics[run.kind] = digest.keyMetrics;
    actionable.push(...extractSuggestions(run.result?.details).map((s, index) => ({
      ...s, analysisKind: run.kind, sourceMessageId: run.messageId, sourceSuggestionIndex: index,
    })));
  }
  return {
    messageId: batchId,
    createdAt: runs[0]?.createdAt || null,
    result: {
      analysisKind: 'all',
      // Full raw reports remain available to the agent, including diagnosis
      // findings that have no executable suggestion.
      analyses: runs,
      details: {
        campaign_id: platformCampaignId,
        analysis: {
          summary: summaries.join('\n\n'), actionable_suggestions: actionable,
          campaign_analysis: { key_insights: insights, key_metrics: keyMetrics },
        },
      },
    },
  };
}

export function appliedAnalysisIndexes(messages, run, options) {
  const sources = new Map();
  return extractSuggestions(run.result?.details).flatMap((s, index) => {
    const id = s.sourceMessageId || run.messageId;
    if (!sources.has(id)) sources.set(id, new Set(appliedSuggestionIndexes(messages, id, options)));
    return sources.get(id).has(s.sourceSuggestionIndex ?? index) ? [index] : [];
  });
}
